<template>
  <div>
    <v-text-field
      v-model="inputDatetime"
      v-mask="inputMask"
      v-bind="$attrs"
      :error-messages="[
        ...(temporaryError || []),
        ...($attrs['error-messages'] || [])
      ]"
      :append-icon="appendIcon"
      @blur="onBlurInputDatetime"
      @paste.prevent="pasteData"
      @click:append="openDatetime"
    >
      <template #label>
        <slot name="label" />
      </template>
    </v-text-field>
    <datetime
      ref="datetime"
      v-model="date"
      value-zone="local"
      :type="datetime ? 'datetime' : 'date'"
      :use-second="useSecond"
      :hour-step="hourStep"
      :minute-step="minuteStep"
      :min-datetime="minDatetime"
      :max-datetime="maxDatetime"
      :week-start="1"
      style="background: rgba(0, 0, 0, 0.2);"
      input-class="vdatetime-hide"
      class="theme-primary"
      :phrases="trans"
    />
  </div>
</template>

<script>
import { DateTime } from "luxon";
import Datetime from "vue-datetime/src/Datetime";
import "vue-datetime/dist/vue-datetime.css";
import SvConstants from "@/utils/Const";

export default {
  components: {
    Datetime
  },
  props: {
    value: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    datetime: {
      type: Boolean,
      default: false
    },
    format: {
      type: [String, Function],
      default: null
    },
    datetimeProps: {
      type: Object,
      default: () => {}
    },
    useSecond: {
      type: Boolean,
      default: true
    },
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    secondStep: {
      type: Number,
      default: 1
    },
    minDatetime: {
      type: String,
      default: null
    },
    maxDatetime: {
      type: String,
      default: null
    },
    formattedModel: {
      type: String,
      default: null
    },
    tmpErrorDuration: {
      type: Number,
      default: 2000
    },
    appendIcon: {
      type: String,
      default: "mdi-calendar-month"
    }
  },
  data() {
    return {
      temporaryError: null,
      inputDatetime: ""
    };
  },
  computed: {
    date: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    },
    trans() {
      return {
        invalidDatetimePaste: isDatetime => {
          return isDatetime
            ? this.$t("DatetimeField.invalidDatetimePaste")
            : this.$t("DatetimeField.invalidDatePaste");
        },
        invalidDatetimeFormat: isDatetime => {
          return isDatetime
            ? this.$t("DatetimeField.invalidDatetimeFormat")
            : this.$t("DatetimeField.invalidDateFormat");
        },
        datetimeLessThanMinLimit: (isDatetime, min) => {
          return isDatetime
            ? this.$t("DatetimeField.datetimeLessThanMinLimit", { min })
            : this.$t("DatetimeField.dateLessThanMinLimit", { min });
        },
        datetimeGreaterThanMaxLimit: (isDatetime, max) => {
          return isDatetime
            ? this.$t("DatetimeField.datetimeGreaterThanMaxLimit", { max })
            : this.$t("DatetimeField.dateGreaterThanMaxLimit", { max });
        },
        ok: "Применить",
        cancel: "Отменить"
      };
    },
    datetimeFormat() {
      const { useSecond, datetime } = this;
      return datetime
        ? useSecond
          ? SvConstants.LuxonFullDateTimeFormat
          : SvConstants.LuxonShortDateTimeFormat
        : SvConstants.LuxonDateFormat;
    },
    inputMask() {
      const { useSecond, datetime } = this;
      return datetime
        ? useSecond
          ? `##.##.#### ##:##:##`
          : "##.##.#### ##:##"
        : "##.##.####";
    }
  },
  watch: {
    date(v) {
      this.onChangeModelDatetime(v);
    },
    maxDatetime() {
      if (!this.date) return;
      if (!this.validateInputDatetime(DateTime.fromISO(this.date)))
        this.resetDate();
    },
    minDatetime() {
      if (!this.date) return;
      if (!this.validateInputDatetime(DateTime.fromISO(this.date)))
        this.resetDate();
    }
  },
  mounted() {
    this.onChangeModelDatetime(this.date);
  },
  methods: {
    setInputDateTime(dt) {
      this.inputDatetime = dt ? dt.toFormat(this.datetimeFormat) : null;
    },
    setModelDatetime(dt) {
      this.date = dt ? dt.toFormat("yyyy-MM-dd") : null;
      this.setFormattedModelDatetime(dt);
    },
    setFormattedModelDatetime(dt) {
      const { format } = this;
      if (!format || !dt) {
        this.updateFormattedDatetime(null);
      } else {
        if (typeof format === "function") {
          this.updateFormattedDatetime(format(dt));
        } else {
          this.updateFormattedDatetime(dt.toFormat(format));
        }
      }
    },
    updateFormattedDatetime(v) {
      this.$emit("update:formattedDatetime", v);
    },
    parseInputDatetime(value) {
      const datetimeRegex = /^(?<day>0?[1-9]|[12][0-9]|3[01])\.(?<month>0?[1-9]|1[0-2])\.(?<year>\d\d\d\d)(\s(?<hour>00|0[0-9]|1[0-9]|2[0-3]):(?<minute>0[0-9]|[0-5][0-9])(:(?<second>0[0-9]|[0-5][0-9]))?)?$/;
      const result = datetimeRegex.exec(value);

      if (!result) return null;
      return DateTime.fromObject(result.groups);
    },
    showTemporaryError(error) {
      this.temporaryError = [error];
      setTimeout(() => (this.temporaryError = null), this.tmpErrorDuration);
    },
    openDatetime(event) {
      if (this.disabled) return;
      this.onBlurInputDatetime();
      this.$nextTick(() => {
        if (event) {
          this.$refs.datetime.$el
            .getElementsByClassName("vdatetime-input")[0]
            .click();
        }
      });
    },
    validateInputDatetime(dt, onPaste = false) {
      let { minDatetime, maxDatetime, trans, datetime, datetimeFormat } = this;

      if (!dt) {
        if (onPaste)
          this.showTemporaryError(trans.invalidDatetimePaste(datetime));
        return false;
      }
      if (!dt.isValid) {
        this.showTemporaryError(trans.invalidDatetimeFormat(datetime));
        return false;
      }

      minDatetime = DateTime.fromISO(minDatetime);
      maxDatetime = DateTime.fromISO(maxDatetime);

      if (minDatetime.isValid) {
        if (dt < minDatetime) {
          this.showTemporaryError(
            trans.datetimeLessThanMinLimit(
              datetime,
              minDatetime.toFormat(datetimeFormat)
            )
          );
          return false;
        }
      }
      if (maxDatetime.isValid) {
        if (dt > maxDatetime) {
          this.showTemporaryError(
            trans.datetimeGreaterThanMaxLimit(
              datetime,
              maxDatetime.toFormat(datetimeFormat)
            )
          );
          return false;
        }
      }
      return true;
    },
    resetDate() {
      this.setInputDateTime(null);
      this.setModelDatetime(null);
    },
    onChangeModelDatetime(v) {
      if (v) {
        const dt = DateTime.fromISO(v);
        if (this.validateInputDatetime(dt)) {
          this.setInputDateTime(dt);
          this.setModelDatetime(dt);
        } else {
          this.resetDate();
        }
      } else {
        this.resetDate();
      }
    },
    onBlurInputDatetime() {
      const dt = this.parseInputDatetime(this.inputDatetime);
      if (this.validateInputDatetime(dt)) {
        this.setInputDateTime(dt);
        this.setModelDatetime(dt);
      } else {
        this.resetDate();
      }
    },
    async pasteData({ clipboardData }) {
      if (this.disabled || !clipboardData) return;

      const txt = clipboardData.getData("Text");
      const dt = this.parseInputDatetime(txt);

      if (this.validateInputDatetime(dt, true)) {
        this.setInputDateTime(dt);
        this.setModelDatetime(dt);
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/branding.scss";
.vdatetime-hide {
  display: none;
}

.theme-primary .vdatetime-calendar__month__day:hover {
  &,
  & > span > span {
    background: $brand-border-inner;
    transition: none;
  }
}

.theme-primary .vdatetime-calendar__month__day--selected {
  &,
  &:hover {
    &,
    & > span > span {
      color: $brand-menu-selected-text;
      background: $brand-text-primary;
      transition: none;
    }
  }
}

.theme-primary .vdatetime-calendar__month__day > span > span {
  user-select: none;
  border-radius: 2px;
}

.theme-primary .vdatetime-year-picker__item--selected,
.theme-primary .vdatetime-time-picker__item--selected,
.theme-primary .vdatetime-month-picker__item--selected,
.theme-primary .vdatetime-popup__actions__button {
  color: $brand-text-primary;
}

.theme-primary .vdatetime-popup__header,
.theme-primary .vdatetime-calendar__month__day--selected {
  background: $brand-text-primary;
}
</style>
